import { FormEvent, useEffect, useMemo, useState } from 'react';

type ProcurementStatus = 'requested' | 'acknowledged' | 'ordered' | 'received' | 'cancelled';
type ProcurementCategory = 'shop_supply' | 'part' | 'print_request' | 'other';

interface OpsNeed {
  line_id: number;
  product_id: number | null;
  name: string | null;
  qty: number | null;
  procurement_status: 'not_ordered' | 'rfq' | 'ordered' | 'received';
  po_names: string | null;
  po_qty: number | null;
  qty_received: number | null;
  po_eta: string | null;
}

interface OpsBuild {
  odoo_sale_order_id: number;
  so_name: string | null;
  partner_name: string | null;
  commitment_date: string | null;
  days_to_promised: number | null;
  blocked: boolean;
  blocked_reasons: string[];
  needs_total: number;
  needs_received: number;
  needs: OpsNeed[];
}

interface OpsBoardPayload {
  generated_at: string;
  builds: OpsBuild[];
}

interface ProcurementRequisition {
  id: number;
  category: ProcurementCategory;
  item_name: string;
  quantity_requested: number | null;
  qty_on_hand: number | null;
  safety_stock: number | null;
  status: ProcurementStatus;
  requested_by: string | null;
  assigned_to: string | null;
  requested_at: string;
  needed_by: string | null;
  date_ordered: string | null;
  eta: string | null;
  source_sheet_id: string | null;
  source_sheet_tab: string | null;
  source_row_number: number | null;
  part_link: string | null;
  comments: string | null;
  created_by_email: string | null;
  updated_by_email: string | null;
  created_at: string;
  updated_at: string;
}

interface RequisitionsPayload {
  generated_at: string;
  requisitions: ProcurementRequisition[];
}

interface FrictionSnippet {
  message_id: string;
  space_display_name: string | null;
  sender_display_name: string | null;
  sender_email: string | null;
  created_at_chat: string;
  message_text: string | null;
}

interface FrictionTheme {
  theme: string;
  count: number;
  snippets: FrictionSnippet[];
}

interface FrictionPayload {
  generated_at: string;
  since_days: number;
  themes: FrictionTheme[];
  unavailable?: string;
}

interface MondayHotItem {
  monday_subitem_id: string;
  parent_item_id: string;
  monday_board_id: string;
  build_name: string | null;
  build_url: string | null;
  group_title: string | null;
  item_name: string | null;
  so_number: string | null;
  service_status: string | null;
  part_status: string | null;
  admin_status: string | null;
  sales_qc: string | null;
  tech: string | null;
  due_date: string | null;
  monday_updated_at: string | null;
  urgency_rank: number;
}

interface MondayHotItemsPayload {
  generated_at: string;
  snapshot_finished_at: string | null;
  hot_items: MondayHotItem[];
  unavailable?: string;
}

type PurchasingSignalType = 'po_sent' | 'vendor_invoice' | 'vendor_estimate' | 'order_update' | 'vendor_message';

interface PurchasingSignal {
  mailbox: string;
  gmail_message_id: string;
  gmail_thread_id: string | null;
  direction: 'inbound' | 'outbound' | 'internal' | 'unknown';
  from_email: string | null;
  from_name: string | null;
  to_emails: string[];
  cc_emails: string[];
  subject: string | null;
  snippet: string | null;
  message_at: string | null;
  label_ids: string[];
  signal_type: PurchasingSignalType;
  urgency_rank: number;
  unread: boolean;
}

interface PurchasingSignalsPayload {
  generated_at: string;
  mailbox: string;
  since_days: number;
  signals: PurchasingSignal[];
  unavailable?: string;
}

interface AtRiskNeed {
  build: OpsBuild;
  need: OpsNeed;
}

interface ProcurementAction {
  id: string;
  source: 'monday' | 'email' | 'odoo' | 'requisition';
  priority: 'critical' | 'high' | 'normal';
  title: string;
  detail: string;
  status: string;
  dueLabel: string;
  href?: string | null;
  sortKey: number;
}

interface RequestDraft {
  key: string;
  category: ProcurementCategory;
  item_name: string;
  requested_by?: string;
  comments?: string;
}

type RequisitionPatch = Partial<
  Pick<
    ProcurementRequisition,
    | 'category'
    | 'item_name'
    | 'quantity_requested'
    | 'qty_on_hand'
    | 'safety_stock'
    | 'status'
    | 'requested_by'
    | 'assigned_to'
    | 'needed_by'
    | 'date_ordered'
    | 'eta'
    | 'part_link'
    | 'comments'
  >
>;

interface OwnerWorkload {
  owner: string | null;
  total: number;
  requested: number;
  ordered: number;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      board: OpsBoardPayload;
      requisitions: RequisitionsPayload;
      friction: FrictionPayload;
      mondayHotItems: MondayHotItemsPayload;
      purchasingSignals: PurchasingSignalsPayload;
    };

const CATEGORY_LABELS: Record<ProcurementCategory, string> = {
  shop_supply: 'Shop supply',
  part: 'Part',
  print_request: '3D print',
  other: 'Other',
};

const STATUS_LABELS: Record<ProcurementStatus, string> = {
  requested: 'Requested',
  acknowledged: 'Acknowledged',
  ordered: 'Ordered',
  received: 'Received',
  cancelled: 'Cancelled',
};

const STATUS_CLASSES: Record<ProcurementStatus, string> = {
  requested: 'bg-rose-100 text-rose-700',
  acknowledged: 'bg-amber-100 text-amber-700',
  ordered: 'bg-sky-100 text-sky-700',
  received: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

const NEED_STATUS_CLASSES: Record<OpsNeed['procurement_status'], string> = {
  not_ordered: 'bg-rose-100 text-rose-700',
  rfq: 'bg-amber-100 text-amber-700',
  ordered: 'bg-sky-100 text-sky-700',
  received: 'bg-emerald-100 text-emerald-700',
};

const FRICTION_LABELS: Record<string, string> = {
  waiting_on_parts: 'Waiting on parts',
  order_now: 'Order/vendor chatter',
  print_queue: '3D print queue',
  monday_or_sheet: 'Monday/sheet friction',
};

const PRIORITY_CLASSES: Record<ProcurementAction['priority'], string> = {
  critical: 'bg-rose-100 text-rose-700',
  high: 'bg-amber-100 text-amber-700',
  normal: 'bg-slate-100 text-slate-600',
};

const SOURCE_LABELS: Record<ProcurementAction['source'], string> = {
  monday: 'Monday',
  email: 'Purchasing email',
  odoo: 'Odoo',
  requisition: 'Request',
};

const PURCHASING_SIGNAL_LABELS: Record<PurchasingSignalType, string> = {
  po_sent: 'PO sent',
  vendor_invoice: 'Vendor invoice',
  vendor_estimate: 'Vendor estimate',
  order_update: 'Order update',
  vendor_message: 'Vendor message',
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  return value.slice(0, 10);
}

function formatQty(value: number | null): string {
  if (value == null) return '-';
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

function parseOptionalNumberInput(value: string): number | null {
  const cleaned = value.replace(/[$,]/g, '').trim();
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function parseNumber(value: string | undefined): number | null {
  if (!value) return null;
  const cleaned = value.replace(/[$,]/g, '').trim();
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function parseSheetDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const m = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!m) return undefined;
  const year = Number(m[3].length === 2 ? `20${m[3]}` : m[3]);
  const month = Number(m[1]);
  const day = Number(m[2]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function compactText(value: string | null | undefined, max = 110): string {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 3)).trim()}...`;
}

function draftFromFrictionSnippet(theme: string, snippet: FrictionSnippet): RequestDraft {
  const message = compactText(snippet.message_text, 180);
  const category: ProcurementCategory =
    theme === 'print_queue' ? 'print_request' : theme === 'monday_or_sheet' ? 'shop_supply' : 'part';
  const sender = snippet.sender_display_name ?? snippet.sender_email ?? undefined;
  const source = [formatDate(snippet.created_at_chat), snippet.space_display_name ?? 'Google Chat', sender].filter(Boolean).join(' · ');
  const draft: RequestDraft = {
    key: `${theme}-${snippet.message_id}`,
    category,
    item_name: compactText(message || FRICTION_LABELS[theme] || 'Chat procurement follow-up', 92),
    comments: `${source}: ${message || '-'}`,
  };
  if (sender) draft.requested_by = sender;
  return draft;
}

function draftFromMondayHotItem(item: MondayHotItem): RequestDraft {
  const parts = [
    item.build_name,
    item.so_number,
    item.part_status ? `Part status: ${item.part_status}` : null,
    item.service_status ? `Service: ${item.service_status}` : null,
    item.tech ? `Tech: ${item.tech}` : null,
  ].filter(Boolean);
  const draft: RequestDraft = {
    key: `monday-${item.monday_subitem_id}`,
    category: item.part_status === 'To 3D Print' ? 'print_request' : 'part',
    item_name: compactText(item.item_name ?? 'Monday procurement item', 92),
    comments: `${parts.join(' · ') || 'Monday hot item'} · Monday subitem ${item.monday_subitem_id}`,
  };
  if (item.tech) draft.requested_by = item.tech;
  return draft;
}

function draftFromPurchasingSignal(signal: PurchasingSignal): RequestDraft {
  const draft: RequestDraft = {
    key: `email-${signal.gmail_message_id}`,
    category: 'part',
    item_name: compactText(signal.subject ?? PURCHASING_SIGNAL_LABELS[signal.signal_type], 92),
    comments: compactText(
      [
        PURCHASING_SIGNAL_LABELS[signal.signal_type],
        signal.unread ? 'Unread' : null,
        formatDate(signal.message_at),
        signal.from_name ?? signal.from_email,
        signal.snippet,
      ]
        .filter(Boolean)
        .join(' · '),
      260,
    ),
  };
  const sender = signal.from_name ?? signal.from_email;
  if (sender) draft.requested_by = sender;
  return draft;
}

function daysUntil(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.ceil((parsed.getTime() - start.getTime()) / 86_400_000);
}

function dueText(days: number | null, fallback: string): string {
  if (days == null) return fallback;
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Due today';
  return `${days}d`;
}

function priorityFromDays(days: number | null): ProcurementAction['priority'] {
  if (days != null && days <= 3) return 'critical';
  if (days != null && days <= 14) return 'high';
  return 'normal';
}

function buildProcurementActions(
  mondayHotItems: MondayHotItem[],
  purchasingSignals: PurchasingSignal[],
  atRiskNeeds: AtRiskNeed[],
  requisitions: ProcurementRequisition[],
): ProcurementAction[] {
  const mondayActions = mondayHotItems.map<ProcurementAction>((item) => {
    const isOrderNow = String(item.part_status ?? '').toUpperCase().includes('ORDER NOW');
    const days = daysUntil(item.due_date);
    const priority: ProcurementAction['priority'] = isOrderNow || item.urgency_rank <= 10 ? 'critical' : item.urgency_rank <= 40 ? 'high' : priorityFromDays(days);
    return {
      id: `monday-${item.monday_subitem_id}`,
      source: 'monday',
      priority,
      title: item.item_name ?? 'Unnamed Monday item',
      detail:
        [item.build_name, item.so_number, item.tech, item.service_status, item.admin_status, item.sales_qc]
          .filter(Boolean)
          .join(' · ') || item.parent_item_id,
      status: item.part_status ?? 'Unlabeled',
      dueLabel: dueText(days, formatDate(item.due_date)),
      href: item.build_url,
      sortKey: (priority === 'critical' ? 0 : priority === 'high' ? 1000 : 2000) + item.urgency_rank,
    };
  });

  const emailActions = purchasingSignals.map<ProcurementAction>((signal) => {
    const criticalSignal =
      signal.unread && (signal.signal_type === 'vendor_invoice' || signal.signal_type === 'vendor_estimate');
    const priority: ProcurementAction['priority'] = criticalSignal ? 'critical' : signal.urgency_rank <= 10 ? 'high' : 'normal';
    return {
      id: `email-${signal.gmail_message_id}`,
      source: 'email',
      priority,
      title: signal.subject ?? PURCHASING_SIGNAL_LABELS[signal.signal_type],
      detail: [signal.from_name ?? signal.from_email, signal.snippet].filter(Boolean).join(' · '),
      status: `${PURCHASING_SIGNAL_LABELS[signal.signal_type]}${signal.unread ? ' · unread' : ''}`,
      dueLabel: formatDate(signal.message_at),
      href: `https://mail.google.com/mail/#all/${signal.gmail_message_id}`,
      sortKey: (priority === 'critical' ? 50 : priority === 'high' ? 1050 : 2050) + signal.urgency_rank,
    };
  });

  const odooActions = atRiskNeeds.map<ProcurementAction>(({ build, need }) => {
    const days = build.days_to_promised;
    const priority = priorityFromDays(days);
    return {
      id: `odoo-${build.odoo_sale_order_id}-${need.line_id}`,
      source: 'odoo',
      priority,
      title: need.name ?? `Product ${need.product_id ?? '?'}`,
      detail: [build.so_name, build.partner_name].filter(Boolean).join(' · ') || String(build.odoo_sale_order_id),
      status: need.procurement_status === 'not_ordered' ? 'Not ordered' : 'RFQ',
      dueLabel: dueText(days, 'Unscheduled'),
      sortKey: (priority === 'critical' ? 100 : priority === 'high' ? 1100 : 2100) + (days ?? 999),
    };
  });

  const requestActions = requisitions
    .filter((req) => req.status !== 'received' && req.status !== 'cancelled')
    .map<ProcurementAction>((req) => {
      const days = daysUntil(req.needed_by);
      const priority = req.status === 'requested' ? priorityFromDays(days ?? 7) : priorityFromDays(days);
      return {
        id: `req-${req.id}`,
        source: 'requisition',
        priority,
        title: req.item_name,
        detail: [req.assigned_to ? `Owner: ${req.assigned_to}` : null, req.requested_by, CATEGORY_LABELS[req.category], req.comments]
          .filter(Boolean)
          .join(' · '),
        status: STATUS_LABELS[req.status],
        dueLabel: dueText(days, formatDate(req.needed_by)),
        href: req.part_link,
        sortKey:
          (priority === 'critical' ? 200 : priority === 'high' ? 1200 : 2200) +
          (req.status === 'requested' ? 0 : req.status === 'acknowledged' ? 50 : 100) +
          (days ?? 999),
      };
    });

  return [...mondayActions, ...emailActions, ...odooActions, ...requestActions]
    .sort((a, b) => a.sortKey - b.sortKey)
    .slice(0, 18);
}

function buildOwnerWorkloads(requisitions: ProcurementRequisition[]): OwnerWorkload[] {
  const workloads = new Map<string, OwnerWorkload>();
  for (const req of requisitions) {
    const key = req.assigned_to?.trim() || '__unassigned__';
    const current = workloads.get(key) ?? {
      owner: key === '__unassigned__' ? null : key,
      total: 0,
      requested: 0,
      ordered: 0,
    };
    current.total += 1;
    if (req.status === 'requested') current.requested += 1;
    if (req.status === 'ordered') current.ordered += 1;
    workloads.set(key, current);
  }
  return Array.from(workloads.values()).sort((a, b) => {
    if (a.owner === null) return -1;
    if (b.owner === null) return 1;
    return b.total - a.total || a.owner.localeCompare(b.owner);
  });
}

function splitSheetRows(text: string): string[][] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0)
    .map((line) => line.split('\t').map((cell) => cell.trim()));
}

function parseLegacySheetRows(
  sourceSheetTab: string,
  text: string,
  firstSourceRow: number,
): Array<Record<string, unknown>> {
  const rawRows = splitSheetRows(text);
  if (rawRows.length === 0) return [];

  const normalizedFirst = rawRows[0].map(normalizeHeader);
  const hasHeader =
    normalizedFirst.includes('part') ||
    normalizedFirst.includes('part_name') ||
    normalizedFirst.includes('qty_needed') ||
    normalizedFirst.includes('quantity_requested');
  const rows = hasHeader ? rawRows.slice(1) : rawRows;
  const headerOffset = hasHeader ? 1 : 0;

  return rows
    .map((cells, index) => {
      const sourceRowNumber = firstSourceRow + index + headerOffset;
      if (sourceSheetTab === 'Shop Supplies') {
        return {
          category: 'shop_supply',
          item_name: cells[0],
          part_link: cells[1],
          qty_on_hand: parseNumber(cells[2]),
          safety_stock: parseNumber(cells[3]),
          quantity_requested: parseNumber(cells[4]),
          requested_by: cells[6],
          date_ordered: parseSheetDate(cells[10]),
          eta: parseSheetDate(cells[11]),
          comments: cells[12],
          source_row_number: sourceRowNumber,
        };
      }
      if (sourceSheetTab === '3D Printing Request') {
        const acknowledged = cells[3] ? `Acknowledged: ${cells[3]}` : undefined;
        return {
          category: 'print_request',
          item_name: cells[0],
          quantity_requested: 1,
          requested_by: cells[2],
          needed_by: parseSheetDate(cells[1]),
          comments: acknowledged,
          source_row_number: sourceRowNumber,
        };
      }
      return {
        category: 'part',
        item_name: cells[0],
        quantity_requested: parseNumber(cells[3]) ?? parseNumber(cells[1]),
        requested_by: undefined,
        needed_by: parseSheetDate(cells[2]),
        date_ordered: parseSheetDate(cells[4]),
        eta: parseSheetDate(cells[5]),
        source_row_number: sourceRowNumber,
      };
    })
    .filter((row) => typeof row.item_name === 'string' && row.item_name.trim().length > 0);
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Unexpected status ${res.status}`);
  }
  return (await res.json()) as T;
}

function statusChip(status: ProcurementStatus): JSX.Element {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

function partStatusChip(status: string | null): JSX.Element {
  const label = status ?? 'Unlabeled';
  const cls = label.includes('ORDER NOW')
    ? 'bg-rose-100 text-rose-700'
    : label === 'To 3D Print'
      ? 'bg-violet-100 text-violet-700'
      : label.toLowerCase().includes('stock') || label.toLowerCase().includes('kitting')
        ? 'bg-amber-100 text-amber-700'
        : 'bg-slate-100 text-slate-600';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

function SummaryTile({ label, value, tone }: { label: string; value: number; tone: string }): JSX.Element {
  return (
    <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
      <div className={`text-2xl font-semibold ${tone}`}>{value}</div>
      <div className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}

function SourceCard({ label, body }: { label: string; body: string }): JSX.Element {
  return (
    <div className="rounded-lg bg-white p-3 ring-1 ring-slate-200">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <p className="mt-1 text-sm leading-5 text-slate-600">{body}</p>
    </div>
  );
}

function OwnerWorkloadBar({
  workloads,
  selectedOwner,
  onSelectOwner,
}: {
  workloads: OwnerWorkload[];
  selectedOwner: string;
  onSelectOwner: (owner: string) => void;
}): JSX.Element {
  const total = workloads.reduce((sum, workload) => sum + workload.total, 0);
  const chipClass = (active: boolean): string =>
    `rounded-md border px-3 py-2 text-left text-xs transition ${
      active
        ? 'border-slate-900 bg-slate-900 text-white'
        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
    }`;
  return (
    <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900">Owner workload</h3>
        <span className="text-xs text-slate-500">{total} open requisition(s)</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => onSelectOwner('all')} className={chipClass(selectedOwner === 'all')}>
          <span className="block font-medium">All</span>
          <span className={selectedOwner === 'all' ? 'text-slate-200' : 'text-slate-500'}>{total} open</span>
        </button>
        {workloads.map((workload) => {
          const value = workload.owner ?? '__unassigned__';
          const label = workload.owner ?? 'Unassigned';
          const active = selectedOwner === value;
          return (
            <button key={value} type="button" onClick={() => onSelectOwner(value)} className={chipClass(active)}>
              <span className="block font-medium">{label}</span>
              <span className={active ? 'text-slate-200' : 'text-slate-500'}>
                {workload.total} open · {workload.requested} new · {workload.ordered} ordered
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ActionQueue({ actions }: { actions: ProcurementAction[] }): JSX.Element {
  return (
    <div className="rounded-lg bg-white ring-1 ring-slate-200">
      <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Priority procurement queue</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Ranked signals from source systems. Use these to decide what to handle; PO creation still happens outside this surface.
          </p>
        </div>
        <span className="text-xs text-slate-500">{actions.length} ranked action(s)</span>
      </div>
      {actions.length === 0 ? (
        <p className="p-4 text-sm text-slate-500">No active procurement actions found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-[820px] w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs text-slate-500">
                <th className="px-4 py-2 font-medium">Priority</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">Action</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Due</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {actions.map((action) => (
                <tr key={action.id}>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_CLASSES[action.priority]}`}>
                      {action.priority === 'critical' ? 'Critical' : action.priority === 'high' ? 'High' : 'Normal'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{SOURCE_LABELS[action.source]}</td>
                  <td className="max-w-[520px] px-3 py-2">
                    <div className="font-medium text-slate-900">
                      {action.href ? (
                        <a
                          href={action.href}
                          target="_blank"
                          rel="noreferrer"
                          className="underline decoration-slate-300 underline-offset-2 hover:text-slate-700"
                        >
                          {action.title}
                        </a>
                      ) : (
                        action.title
                      )}
                    </div>
                    <div className="truncate text-xs text-slate-500">{action.detail || '-'}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{action.status}</td>
                  <td className="px-3 py-2 text-slate-600">{action.dueLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RequestForm({
  draft,
  onCreated,
}: {
  draft: RequestDraft | null;
  onCreated: (req: ProcurementRequisition) => void;
}): JSX.Element {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const itemName = String(form.get('item_name') ?? '').trim();
    if (!itemName) {
      setError('Item name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const quantityRaw = String(form.get('quantity_requested') ?? '').trim();
      const payload = {
        category: form.get('category'),
        item_name: itemName,
        quantity_requested: quantityRaw ? Number(quantityRaw) : null,
        needed_by: String(form.get('needed_by') ?? '').trim() || undefined,
        requested_by: String(form.get('requested_by') ?? '').trim() || undefined,
        assigned_to: String(form.get('assigned_to') ?? '').trim() || undefined,
        part_link: String(form.get('part_link') ?? '').trim() || undefined,
        comments: String(form.get('comments') ?? '').trim() || undefined,
      };
      const result = await fetchJson<{ requisition: ProcurementRequisition }>('/api/ops/requisitions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      event.currentTarget.reset();
      onCreated(result.requisition);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Capture missing need</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Creates a Sales-QA requisition record. It does not create a purchase order.
          </p>
        </div>
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
        >
          {saving ? 'Saving' : 'Add request'}
        </button>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-[1fr_130px_120px_150px]">
        <label className="text-xs font-medium text-slate-600">
          Item
          <input
            name="item_name"
            defaultValue={draft?.item_name ?? ''}
            className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
            placeholder="Part, shop supply, or print"
          />
        </label>
        <label className="text-xs font-medium text-slate-600">
          Category
          <select
            name="category"
            defaultValue={draft?.category ?? 'part'}
            className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
          >
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-medium text-slate-600">
          Qty
          <input
            name="quantity_requested"
            type="number"
            min="0"
            step="0.01"
            className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
          />
        </label>
        <label className="text-xs font-medium text-slate-600">
          Needed by
          <input
            name="needed_by"
            type="date"
            className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
          />
        </label>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-[170px_170px_1fr]">
        <label className="text-xs font-medium text-slate-600">
          Requested by
          <input
            name="requested_by"
            defaultValue={draft?.requested_by ?? ''}
            className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
            placeholder="Name"
          />
        </label>
        <label className="text-xs font-medium text-slate-600">
          Owner
          <input
            name="assigned_to"
            className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
            placeholder="Buyer"
          />
        </label>
        <label className="text-xs font-medium text-slate-600">
          Buy link
          <input
            name="part_link"
            type="url"
            className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
            placeholder="https:// vendor, Amazon, PO, or product page"
          />
        </label>
      </div>
      <div className="mt-3">
        <label className="text-xs font-medium text-slate-600">
          Notes
          <input
            name="comments"
            defaultValue={draft?.comments ?? ''}
            className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
            placeholder="Vendor, link, build, or urgency"
          />
        </label>
      </div>
      {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
    </form>
  );
}

function LegacySheetImportPanel({
  onImported,
}: {
  onImported: (reqs: ProcurementRequisition[]) => void;
}): JSX.Element {
  const [sourceSheetTab, setSourceSheetTab] = useState('Shop Supplies');
  const [firstSourceRow, setFirstSourceRow] = useState(2);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const rows = parseLegacySheetRows(sourceSheetTab, text, firstSourceRow);
    if (rows.length === 0) {
      setError('No importable rows found.');
      setMessage(null);
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const result = await fetchJson<{ imported_count: number; requisitions: ProcurementRequisition[] }>(
        '/api/ops/requisitions/import',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source_sheet_tab: sourceSheetTab, rows }),
        },
      );
      onImported(result.requisitions);
      setMessage(`${result.imported_count} row(s) imported.`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className="rounded-lg bg-white ring-1 ring-slate-200">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-slate-900">
        Legacy sheet import
      </summary>
      <form onSubmit={(event) => void submit(event)} className="border-t border-slate-100 px-4 pb-4 pt-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          Maintenance path for moving old Supplies Needed rows into app-owned requisitions.
        </p>
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
        >
          {saving ? 'Importing' : 'Import'}
        </button>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_110px]">
        <label className="text-xs font-medium text-slate-600">
          Tab
          <select
            value={sourceSheetTab}
            onChange={(event) => setSourceSheetTab(event.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
          >
            <option value="Shop Supplies">Shop Supplies</option>
            <option value="Parts Needed">Parts Needed</option>
            <option value="3D Printing Request">3D Printing Request</option>
          </select>
        </label>
        <label className="text-xs font-medium text-slate-600">
          First row
          <input
            value={firstSourceRow}
            onChange={(event) => setFirstSourceRow(Number(event.target.value) || 2)}
            type="number"
            min="1"
            className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm text-slate-900 outline-none focus:border-slate-500"
          />
        </label>
      </div>
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        className="mt-3 h-28 w-full rounded-md border border-slate-300 px-2.5 py-2 font-mono text-xs text-slate-900 outline-none focus:border-slate-500"
        placeholder="Paste copied sheet rows"
      />
      {message && <p className="mt-2 text-xs text-emerald-700">{message}</p>}
      {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
      </form>
    </details>
  );
}

function RequisitionTable({
  requisitions,
  onUpdated,
}: {
  requisitions: ProcurementRequisition[];
  onUpdated: (req: ProcurementRequisition) => void;
}): JSX.Element {
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  async function patchRequisition(req: ProcurementRequisition, patch: RequisitionPatch): Promise<void> {
    setUpdatingId(req.id);
    try {
      const result = await fetchJson<{ requisition: ProcurementRequisition }>(`/api/ops/requisitions/${req.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      onUpdated(result.requisition);
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div className="rounded-lg bg-white ring-1 ring-slate-200">
      <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">App-owned requisition queue</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Status and field edits update this request queue only; PO creation still happens outside this surface.
          </p>
        </div>
        <span className="shrink-0 text-xs text-slate-500">{requisitions.length} open or recent</span>
      </div>
      {requisitions.length === 0 ? (
        <p className="p-4 text-sm text-slate-500">No app-owned requisitions yet. Add one above to get it out of chat/sheet drift.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-[1120px] w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs text-slate-500">
                <th className="px-4 py-2 font-medium">Item</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Qty</th>
                <th className="px-3 py-2 font-medium">On hand</th>
                <th className="px-3 py-2 font-medium">Safety</th>
                <th className="px-3 py-2 font-medium">Needed</th>
                <th className="px-3 py-2 font-medium">Owner</th>
                <th className="px-3 py-2 font-medium">Current</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Ordered</th>
                <th className="px-3 py-2 font-medium">ETA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {requisitions.map((req) => (
                <tr key={req.id}>
                  <td className="min-w-[340px] max-w-[420px] px-4 py-2">
                    <input
                      key={`${req.id}-item-${req.updated_at}`}
                      defaultValue={req.item_name}
                      disabled={updatingId === req.id}
                      onBlur={(event) => {
                        const next = event.currentTarget.value.trim();
                        if (next && next !== req.item_name) void patchRequisition(req, { item_name: next });
                      }}
                      className="w-full rounded-md border border-transparent bg-transparent px-1 py-1 text-sm font-medium text-slate-900 outline-none hover:border-slate-200 hover:bg-white focus:border-slate-500 disabled:bg-slate-100"
                    />
                    <input
                      key={`${req.id}-link-${req.updated_at}`}
                      defaultValue={req.part_link ?? ''}
                      disabled={updatingId === req.id}
                      onBlur={(event) => {
                        const next = event.currentTarget.value.trim();
                        if (next !== (req.part_link ?? '')) void patchRequisition(req, { part_link: next || null });
                      }}
                      className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 outline-none focus:border-slate-500 disabled:bg-slate-100"
                      placeholder="Buy link / vendor URL"
                    />
                    <textarea
                      key={`${req.id}-comments-${req.updated_at}`}
                      defaultValue={req.comments ?? ''}
                      disabled={updatingId === req.id}
                      onBlur={(event) => {
                        const next = event.currentTarget.value.trim();
                        if (next !== (req.comments ?? '')) void patchRequisition(req, { comments: next || null });
                      }}
                      className="mt-1 h-14 w-full resize-none rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 outline-none focus:border-slate-500 disabled:bg-slate-100"
                      placeholder={req.requested_by ? `Requested by ${req.requested_by}` : 'Notes / vendor context'}
                    />
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                      {req.part_link && (
                        <a href={req.part_link} target="_blank" rel="noreferrer" className="text-slate-600 underline decoration-slate-300 underline-offset-2">
                          Open link
                        </a>
                      )}
                      <span>{req.requested_by ?? req.created_by_email ?? 'Unknown requester'}</span>
                      {(req.source_sheet_tab || req.source_row_number) && (
                        <span>
                          {req.source_sheet_tab ?? 'Sheet'} row {req.source_row_number ?? '?'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{CATEGORY_LABELS[req.category]}</td>
                  <td className="px-3 py-2">
                    <input
                      key={`${req.id}-qty-${req.updated_at}`}
                      type="number"
                      step="any"
                      defaultValue={req.quantity_requested ?? ''}
                      disabled={updatingId === req.id}
                      onBlur={(event) => {
                        const next = parseOptionalNumberInput(event.currentTarget.value);
                        if (next !== req.quantity_requested) void patchRequisition(req, { quantity_requested: next });
                      }}
                      className="w-20 rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-slate-500 disabled:bg-slate-100"
                      placeholder="Qty"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      key={`${req.id}-on-hand-${req.updated_at}`}
                      type="number"
                      step="any"
                      defaultValue={req.qty_on_hand ?? ''}
                      disabled={updatingId === req.id}
                      onBlur={(event) => {
                        const next = parseOptionalNumberInput(event.currentTarget.value);
                        if (next !== req.qty_on_hand) void patchRequisition(req, { qty_on_hand: next });
                      }}
                      className="w-20 rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-slate-500 disabled:bg-slate-100"
                      placeholder="Have"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      key={`${req.id}-safety-${req.updated_at}`}
                      type="number"
                      step="any"
                      defaultValue={req.safety_stock ?? ''}
                      disabled={updatingId === req.id}
                      onBlur={(event) => {
                        const next = parseOptionalNumberInput(event.currentTarget.value);
                        if (next !== req.safety_stock) void patchRequisition(req, { safety_stock: next });
                      }}
                      className="w-20 rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-slate-500 disabled:bg-slate-100"
                      placeholder="Min"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="date"
                      value={req.needed_by ?? ''}
                      disabled={updatingId === req.id}
                      onChange={(event) => void patchRequisition(req, { needed_by: event.target.value || null })}
                      className="w-32 rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-slate-500 disabled:bg-slate-100"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      key={`${req.id}-owner-${req.updated_at}`}
                      defaultValue={req.assigned_to ?? ''}
                      disabled={updatingId === req.id}
                      onBlur={(event) => {
                        const next = event.currentTarget.value.trim();
                        if (next !== (req.assigned_to ?? '')) void patchRequisition(req, { assigned_to: next || null });
                      }}
                      className="w-28 rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-slate-500 disabled:bg-slate-100"
                      placeholder="Owner"
                    />
                  </td>
                  <td className="px-3 py-2">{statusChip(req.status)}</td>
                  <td className="px-3 py-2">
                    <select
                      value={req.status}
                      disabled={updatingId === req.id}
                      onChange={(event) => void patchRequisition(req, { status: event.target.value as ProcurementStatus })}
                      className="w-32 rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-slate-500 disabled:bg-slate-100"
                    >
                      {Object.entries(STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="date"
                      value={req.date_ordered ?? ''}
                      disabled={updatingId === req.id}
                      onChange={(event) => void patchRequisition(req, { date_ordered: event.target.value || null })}
                      className="w-32 rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-slate-500 disabled:bg-slate-100"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="date"
                      value={req.eta ?? ''}
                      disabled={updatingId === req.id}
                      onChange={(event) => void patchRequisition(req, { eta: event.target.value || null })}
                      className="w-32 rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-slate-500 disabled:bg-slate-100"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AtRiskNeedsTable({ needs }: { needs: AtRiskNeed[] }): JSX.Element {
  return (
    <div className="rounded-lg bg-white ring-1 ring-slate-200">
      <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Odoo material lines needing action</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Read-only projection of not-ordered and RFQ lines from active builds.
          </p>
        </div>
        <span className="shrink-0 text-xs text-slate-500">{needs.length} line(s)</span>
      </div>
      {needs.length === 0 ? (
        <p className="p-4 text-sm text-slate-500">No not-ordered or RFQ material lines in the ops projection.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-[820px] w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs text-slate-500">
                <th className="px-4 py-2 font-medium">Build</th>
                <th className="px-3 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 font-medium">Needed line</th>
                <th className="px-3 py-2 font-medium">Qty</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Promised</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {needs.map(({ build, need }) => (
                <tr key={`${build.odoo_sale_order_id}-${need.line_id}`}>
                  <td className="px-4 py-2 font-medium text-slate-900">{build.so_name ?? build.odoo_sale_order_id}</td>
                  <td className="px-3 py-2 text-slate-600">{build.partner_name ?? '-'}</td>
                  <td className="max-w-[420px] px-3 py-2 text-slate-800">{need.name ?? `Product ${need.product_id ?? '?'}`}</td>
                  <td className="px-3 py-2 text-slate-600">{formatQty(need.qty)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${NEED_STATUS_CLASSES[need.procurement_status]}`}>
                      {need.procurement_status === 'not_ordered' ? 'Not ordered' : 'RFQ'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {build.days_to_promised == null ? 'Unscheduled' : `${build.days_to_promised}d`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MondayHotItemsTable({
  payload,
  onDraftRequest,
}: {
  payload: MondayHotItemsPayload;
  onDraftRequest: (draft: RequestDraft) => void;
}): JSX.Element {
  return (
    <div className="rounded-lg bg-white ring-1 ring-slate-200">
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Monday hot procurement signals</h3>
          <p className="mt-0.5 text-xs text-slate-500">Read-only rows from the latest Monday snapshot.</p>
        </div>
        <span className="text-xs text-slate-500">
          {payload.snapshot_finished_at ? `snapshot ${formatDate(payload.snapshot_finished_at)}` : 'snapshot pending'}
        </span>
      </div>
      {payload.unavailable ? (
        <p className="p-4 text-sm text-amber-700">{payload.unavailable}</p>
      ) : payload.hot_items.length === 0 ? (
        <p className="p-4 text-sm text-slate-500">No Monday hot procurement or 3D-print rows in the latest snapshot.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-[920px] w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs text-slate-500">
                <th className="px-4 py-2 font-medium">Build</th>
                <th className="px-3 py-2 font-medium">Item</th>
                <th className="px-3 py-2 font-medium">Part status</th>
                <th className="px-3 py-2 font-medium">Service</th>
                <th className="px-3 py-2 font-medium">Tech</th>
                <th className="px-3 py-2 font-medium">Due</th>
                <th className="px-3 py-2 font-medium">Track</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payload.hot_items.map((item) => (
                <tr key={item.monday_subitem_id}>
                  <td className="max-w-[240px] px-4 py-2">
                    {item.build_url ? (
                      <a
                        href={item.build_url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-slate-900 underline decoration-slate-300 underline-offset-2 hover:text-slate-700"
                      >
                        {item.build_name ?? item.parent_item_id}
                      </a>
                    ) : (
                      <span className="font-medium text-slate-900">{item.build_name ?? item.parent_item_id}</span>
                    )}
                    <div className="truncate text-xs text-slate-500">{item.so_number ?? item.group_title ?? '-'}</div>
                  </td>
                  <td className="max-w-[420px] px-3 py-2 text-slate-800">{item.item_name ?? '-'}</td>
                  <td className="px-3 py-2">{partStatusChip(item.part_status)}</td>
                  <td className="px-3 py-2 text-slate-600">{item.service_status ?? '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{item.tech ?? '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{formatDate(item.due_date)}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => onDraftRequest(draftFromMondayHotItem(item))}
                      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
                    >
                      Draft request
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PurchasingSignalsPanel({
  payload,
  onDraftRequest,
}: {
  payload: PurchasingSignalsPayload;
  onDraftRequest: (draft: RequestDraft) => void;
}): JSX.Element {
  const openSignals = payload.signals.filter(
    (signal) => signal.signal_type === 'vendor_invoice' || signal.signal_type === 'vendor_estimate' || signal.signal_type === 'po_sent',
  );
  return (
    <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Purchasing email signals</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Mirrored mailbox evidence. Drafting a request does not order parts.
          </p>
        </div>
        <span className="text-xs text-slate-500">{payload.since_days}d · {payload.mailbox}</span>
      </div>
      {payload.unavailable ? (
        <p className="mt-3 text-sm text-amber-700">{payload.unavailable}</p>
      ) : openSignals.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No PO, estimate, or invoice triggers found in the purchasing mailbox mirror.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {openSignals.slice(0, 8).map((signal) => (
            <div
              key={signal.gmail_message_id}
              className="rounded-md bg-slate-50 px-3 py-2"
            >
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span
                  className={`rounded-full px-2 py-0.5 font-medium ${
                    signal.unread && (signal.signal_type === 'vendor_invoice' || signal.signal_type === 'vendor_estimate')
                      ? 'bg-rose-100 text-rose-700'
                      : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  {PURCHASING_SIGNAL_LABELS[signal.signal_type]}
                  {signal.unread ? ' · unread' : ''}
                </span>
                <span className="text-slate-500">{formatDate(signal.message_at)}</span>
                <a
                  href={`https://mail.google.com/mail/#all/${signal.gmail_message_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
                >
                  Open email
                </a>
                <button
                  type="button"
                  onClick={() => onDraftRequest(draftFromPurchasingSignal(signal))}
                  className="ml-auto rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
                >
                  Draft request
                </button>
              </div>
              <div className="mt-1 line-clamp-1 text-sm font-medium text-slate-900">
                {signal.subject ?? PURCHASING_SIGNAL_LABELS[signal.signal_type]}
              </div>
              <div className="mt-1 line-clamp-2 text-xs text-slate-600">
                {[signal.from_name ?? signal.from_email, signal.snippet].filter(Boolean).join(' · ') || '-'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FrictionPanel({
  friction,
  onDraftRequest,
}: {
  friction: FrictionPayload;
  onDraftRequest: (draft: RequestDraft) => void;
}): JSX.Element {
  return (
    <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Chat evidence signals</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Terms found in mirrored Google Chat. Use as evidence, not task state.
          </p>
        </div>
        <span className="text-xs text-slate-500">{friction.since_days}d lookback</span>
      </div>
      {friction.unavailable ? (
        <p className="mt-3 text-sm text-amber-700">{friction.unavailable}</p>
      ) : friction.themes.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No procurement terms found in the mirrored Google Chat corpus.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {friction.themes.map((theme) => (
            <div key={theme.theme} className="border-t border-slate-100 pt-3 first:border-t-0 first:pt-0">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium text-slate-800">{FRICTION_LABELS[theme.theme] ?? theme.theme}</div>
                <div className="text-xs text-slate-500">{theme.count} hit(s)</div>
              </div>
              <div className="mt-2 space-y-2">
                {theme.snippets.slice(0, 2).map((snippet) => (
                  <div key={snippet.message_id} className="rounded-md bg-slate-50 px-3 py-2">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                      <span>{formatDate(snippet.created_at_chat)}</span>
                      <span>{snippet.space_display_name ?? 'Google Chat'}</span>
                      <span>{snippet.sender_display_name ?? snippet.sender_email ?? 'Unknown sender'}</span>
                      <button
                        type="button"
                        onClick={() => onDraftRequest(draftFromFrictionSnippet(theme.theme, snippet))}
                        className="ml-auto rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
                      >
                        Draft request
                      </button>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-slate-700">{snippet.message_text ?? '-'}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LegacySheetPanel(): JSX.Element {
  return (
    <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
      <h3 className="text-sm font-semibold text-slate-900">Legacy sheet intake map</h3>
      <p className="mt-1 text-xs text-slate-500">Reference for the tucked maintenance import above.</p>
      <dl className="mt-3 space-y-3 text-sm">
        <div>
          <dt className="font-medium text-slate-800">Shop Supplies</dt>
          <dd className="text-xs text-slate-500">On-hand, safety stock, needed qty, order date, ETA, comments.</dd>
        </div>
        <div>
          <dt className="font-medium text-slate-800">Parts Needed</dt>
          <dd className="text-xs text-slate-500">Part, count, request date, requested qty, ordered date, arrival date.</dd>
        </div>
        <div>
          <dt className="font-medium text-slate-800">3D Printing Request</dt>
          <dd className="text-xs text-slate-500">Part name, request date, requester, Erik acknowledged flag.</dd>
        </div>
      </dl>
    </div>
  );
}

export function ProcurementHub(): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [requestDraft, setRequestDraft] = useState<RequestDraft | null>(null);
  const [selectedOwner, setSelectedOwner] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [board, requisitions, friction, mondayHotItems, purchasingSignals] = await Promise.all([
          fetchJson<OpsBoardPayload>('/api/ops/board'),
          fetchJson<RequisitionsPayload>('/api/ops/requisitions'),
          fetchJson<FrictionPayload>('/api/ops/friction'),
          fetchJson<MondayHotItemsPayload>('/api/ops/monday-hot-items').catch((err) => ({
            generated_at: new Date().toISOString(),
            snapshot_finished_at: null,
            hot_items: [],
            unavailable: (err as Error).message,
          })),
          fetchJson<PurchasingSignalsPayload>('/api/ops/purchasing-signals').catch((err) => ({
            generated_at: new Date().toISOString(),
            mailbox: 'purchasing@overlanddesignbuild.com',
            since_days: 45,
            signals: [],
            unavailable: (err as Error).message,
          })),
        ]);
        if (!cancelled) setState({ kind: 'ready', board, requisitions, friction, mondayHotItems, purchasingSignals });
      } catch (err) {
        if (!cancelled) setState({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const atRiskNeeds = useMemo<AtRiskNeed[]>(() => {
    if (state.kind !== 'ready') return [];
    return state.board.builds
      .flatMap((build) =>
        build.needs
          .filter((need) => need.procurement_status === 'not_ordered' || need.procurement_status === 'rfq')
          .map((need) => ({ build, need })),
      )
      .sort((a, b) => {
        const aDays = a.build.days_to_promised ?? 9999;
        const bDays = b.build.days_to_promised ?? 9999;
        return aDays - bDays;
      })
      .slice(0, 80);
  }, [state]);

  if (state.kind === 'loading') {
    return <p className="text-slate-500">Loading procurement...</p>;
  }

  if (state.kind === 'error') {
    return (
      <div className="rounded-lg bg-white p-6 ring-1 ring-slate-200">
        <h2 className="text-lg font-semibold text-slate-900">Procurement</h2>
        <p className="mt-2 text-sm text-rose-600">Error: {state.message}</p>
      </div>
    );
  }

  const openRequisitions = state.requisitions.requisitions.filter((req) => req.status !== 'received' && req.status !== 'cancelled');
  const requestedCount = openRequisitions.filter((req) => req.status === 'requested').length;
  const orderedCount = openRequisitions.filter((req) => req.status === 'ordered').length;
  const actionQueue = buildProcurementActions(
    state.mondayHotItems.hot_items,
    state.purchasingSignals.signals,
    atRiskNeeds,
    openRequisitions,
  );
  const ownerWorkloads = buildOwnerWorkloads(openRequisitions);
  const visibleRequisitions =
    selectedOwner === 'all'
      ? state.requisitions.requisitions
      : state.requisitions.requisitions.filter((req) =>
          selectedOwner === '__unassigned__' ? !req.assigned_to?.trim() : req.assigned_to?.trim() === selectedOwner,
        );

  function upsertRequisition(req: ProcurementRequisition): void {
    setState((current) => {
      if (current.kind !== 'ready') return current;
      const existing = current.requisitions.requisitions.filter((row) => row.id !== req.id);
      return {
        ...current,
        requisitions: {
          ...current.requisitions,
          requisitions: [req, ...existing],
        },
      };
    });
  }

  function handleCreated(req: ProcurementRequisition): void {
    upsertRequisition(req);
    setRequestDraft(null);
  }

  function upsertRequisitions(reqs: ProcurementRequisition[]): void {
    setState((current) => {
      if (current.kind !== 'ready') return current;
      const importedIds = new Set(reqs.map((req) => req.id));
      const kept = current.requisitions.requisitions.filter((row) => !importedIds.has(row.id));
      return {
        ...current,
        requisitions: {
          ...current.requisitions,
          requisitions: [...reqs, ...kept],
        },
      };
    });
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-900">Procurement triage</h2>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
              Mixed-source ops view
            </span>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            One place to see procurement risk, capture missing requests, and reconcile old sheet/chat/Monday/email
            signals. Purchase orders and Odoo procurement records remain outside this UI.
          </p>
        </div>
        <p className="ml-auto text-xs text-slate-400">
          ops as of {state.board.generated_at.slice(0, 19).replace('T', ' ')}
        </p>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-5">
        <SourceCard label="Monday" body="Snapshot rows that look urgent or print/procurement-related." />
        <SourceCard label="Purchasing email" body="Mailbox signals that may need conversion into requisitions." />
        <SourceCard label="Odoo" body="Active build material lines with not-ordered or RFQ status." />
        <SourceCard label="Sales-QA" body={`${openRequisitions.length} app-owned open requisition(s).`} />
        <SourceCard label="Google Chat" body="Evidence snippets that mention parts, ordering, ETAs, or supplies." />
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-6">
        <SummaryTile label="Monday hot" value={state.mondayHotItems.hot_items.length} tone="text-rose-700" />
        <SummaryTile label="Purchasing mail" value={state.purchasingSignals.signals.length} tone="text-rose-700" />
        <SummaryTile label="Need order/RFQ" value={atRiskNeeds.length} tone="text-amber-700" />
        <SummaryTile label="Requested" value={requestedCount} tone="text-amber-700" />
        <SummaryTile label="Ordered" value={orderedCount} tone="text-sky-700" />
        <SummaryTile label="Chat signals" value={state.friction.themes.reduce((sum, theme) => sum + theme.count, 0)} tone="text-slate-800" />
      </div>

      <div className="mt-4">
        <ActionQueue actions={actionQueue} />
      </div>

      <div className="mt-4">
        <OwnerWorkloadBar workloads={ownerWorkloads} selectedOwner={selectedOwner} onSelectOwner={setSelectedOwner} />
      </div>

      <div className="mt-4">
        <RequestForm key={requestDraft?.key ?? 'blank-request'} draft={requestDraft} onCreated={handleCreated} />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_340px]">
        <div className="space-y-4">
          <MondayHotItemsTable payload={state.mondayHotItems} onDraftRequest={setRequestDraft} />
          <AtRiskNeedsTable needs={atRiskNeeds} />
          <RequisitionTable requisitions={visibleRequisitions} onUpdated={upsertRequisition} />
        </div>
        <div className="space-y-4">
          <PurchasingSignalsPanel payload={state.purchasingSignals} onDraftRequest={setRequestDraft} />
          <FrictionPanel friction={state.friction} onDraftRequest={setRequestDraft} />
          <LegacySheetImportPanel onImported={upsertRequisitions} />
          <LegacySheetPanel />
        </div>
      </div>
    </div>
  );
}
